import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FlexCol } from '@/components/ui/flex';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { DocumentTypeBadge } from '@/pages/RegulatoryWatch/components/columns';
import { UPDATE_TYPE_LABELS } from '@/pages/RegulatoryWatch/labels';
import {
  useAdminDeleteVersionFileService,
  useAdminDeleteVersionService,
  useAdminReferenceDetailService,
  useAdminSourcesService,
  useAdminUpdateReferenceDetailService,
  useAdminUploadVersionFileService,
} from '@/services/backoffice';
import { cn } from '@/utils/classUtils';
import type {
  AdminReferenceDetailResponse,
  AdminReferenceDetailVersion,
  UpdateAdminReferenceDetailBody,
} from '@ditto/shared';
import { VERSION_STATUSES, UPDATE_TYPES, SOURCE_TYPES } from '@ditto/shared';
import dayjs from 'dayjs';
import {
  ArrowLeft,
  Ban,
  Check,
  ChevronRight,
  Copy,
  FileText,
  FileUp,
  MoreHorizontal,
  Plus,
  Star,
  Trash2,
  Upload,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type FC,
  type SetStateAction,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ADMIN_PATH, adminPaths, adminRegulatoryWatchPaths } from '@/paths';
import { CreateVersionModal } from '../../components/RegulatoryWatchAdmin/CreateVersionModal';
import { DatePickerInput } from '../../components/RegulatoryWatchAdmin/DatePickerInput';
import { SourceCombobox } from '../../components/RegulatoryWatchAdmin/SourceCombobox';

const UpdateTypeBadge: FC<{ updateType: string }> = ({ updateType }) => {
  const label = UPDATE_TYPE_LABELS[updateType] ?? updateType;
  switch (updateType) {
    case 'new':
      return (
        <Badge className="h-6 gap-1 border-transparent bg-green-100 text-neutral-900 shadow-none">
          <Star className="h-3 w-3" />
          {label}
        </Badge>
      );
    case 'revision':
      return (
        <Badge className="h-6 gap-1 border-transparent bg-yellow-100 text-neutral-900 shadow-none">
          <Copy className="h-3 w-3" />
          {label}
        </Badge>
      );
    case 'withdrawal':
      return (
        <Badge className="h-6 gap-1 border-transparent bg-red-100 text-neutral-900 shadow-none">
          <Ban className="h-3 w-3" />
          {label}
        </Badge>
      );
    case 'status_change':
      return (
        <Badge className="h-6 gap-1 border-transparent bg-blue-100 text-neutral-900 shadow-none">
          <span className="font-bold">&raquo;</span>
          {label}
        </Badge>
      );
    default:
      return (
        <Badge color="purple" className="gap-1">
          {label}
        </Badge>
      );
  }
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-orange-100',
  final: 'bg-green-100',
  withdrawn: 'bg-red-100',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  final: 'Final',
  withdrawn: 'Withdrawn',
  unknown: 'Unknown',
};

const StatusBadge: FC<{ status: string }> = ({ status }) => {
  const label = STATUS_LABELS[status] ?? status.replace('_', ' ');
  const color = STATUS_COLORS[status] ?? 'bg-neutral-100';
  return (
    <Badge
      className={cn(
        'h-6 border-transparent text-neutral-900 shadow-none',
        color
      )}
    >
      {label}
    </Badge>
  );
};

const StatusBadgeSelect: FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className="cursor-pointer"
        >
          <StatusBadge status={value} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <div className="max-h-[280px] overflow-y-auto py-1">
          {VERSION_STATUSES.filter(s => s !== 'unknown').map(s => (
            <button
              key={s}
              onClick={() => {
                onChange(s);
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-1.5 text-sm',
                'hover:bg-accent focus-visible:outline-none focus-visible:bg-accent'
              )}
            >
              <Check
                className={cn(
                  'h-4 w-4 shrink-0',
                  value === s ? 'opacity-100' : 'opacity-0'
                )}
              />
              <StatusBadge status={s} />
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const UpdateTypeBadgeSelect: FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className="cursor-pointer"
        >
          <UpdateTypeBadge updateType={value} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <div className="max-h-[280px] overflow-y-auto py-1">
          {UPDATE_TYPES.filter(t => t !== 'unknown').map(t => (
            <button
              key={t}
              onClick={() => {
                onChange(t);
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-1.5 text-sm',
                'hover:bg-accent focus-visible:outline-none focus-visible:bg-accent'
              )}
            >
              <Check
                className={cn(
                  'h-4 w-4 shrink-0',
                  value === t ? 'opacity-100' : 'opacity-0'
                )}
              />
              <UpdateTypeBadge updateType={t} />
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const TypeBadgeSelect: FC<{
  value: string | null;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className="cursor-pointer"
        >
          {value ? (
            <DocumentTypeBadge type={value} />
          ) : (
            <Badge variant="outline" className="text-neutral-500">
              Select type
            </Badge>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <div className="max-h-[280px] overflow-y-auto py-1">
          {SOURCE_TYPES.filter(t => t !== 'unknown').map(t => (
            <button
              key={t}
              onClick={() => {
                onChange(t);
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-1.5 text-sm',
                'hover:bg-accent focus-visible:outline-none focus-visible:bg-accent'
              )}
            >
              <Check
                className={cn(
                  'h-4 w-4 shrink-0',
                  value === t ? 'opacity-100' : 'opacity-0'
                )}
              />
              <DocumentTypeBadge type={t} />
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

type VersionEditFields = {
  status?: string;
  effectiveDate?: string | null;
  url?: string | null;
};

type UpdateEditFields = {
  updateType?: string;
  shortSummary?: string | null;
  summary?: string | null;
  publicationDate?: string;
};

type RefEditState = {
  title: string;
  type: string | null;
  source: string;
  url: string;
  description: string;
};

export const AdminEditReferencePage: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { referenceId } = useParams<{ referenceId: string }>();

  const { detail, isLoading } = useAdminReferenceDetailService(referenceId);
  const { updateReferenceDetail, isPending: isSaving } =
    useAdminUpdateReferenceDetailService();

  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    null
  );
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const { deleteVersion, isPending: isDeleting } =
    useAdminDeleteVersionService();

  const [refEdit, setRefEdit] = useState<RefEditState>({
    title: '',
    type: null,
    source: '',
    url: '',
    description: '',
  });
  const [versionEdits, setVersionEdits] = useState<
    Record<string, VersionEditFields>
  >({});
  const [updateEdits, setUpdateEdits] = useState<
    Record<string, UpdateEditFields>
  >({});

  useEffect(() => {
    if (!detail) return;
    setRefEdit({
      title: detail.title,
      type: detail.type ?? null,
      source: detail.source.id,
      url: detail.url ?? '',
      description: detail.description ?? '',
    });
    setVersionEdits({});
    setUpdateEdits({});
  }, [detail]);

  const versions = detail?.versions ?? [];
  const selectedVersion =
    versions.find(v => v.id === selectedVersionId) ?? versions[0] ?? null;

  useEffect(() => {
    setSelectedVersionId(null);
  }, [referenceId]);

  const getVersionField = useCallback(
    <K extends keyof VersionEditFields>(
      versionId: string,
      field: K,
      original: VersionEditFields[K]
    ): NonNullable<VersionEditFields[K]> | VersionEditFields[K] => {
      const edit = versionEdits[versionId];
      if (edit && field in edit) return edit[field] as VersionEditFields[K];
      return original;
    },
    [versionEdits]
  );

  const getUpdateField = useCallback(
    <K extends keyof UpdateEditFields>(
      updateId: string,
      field: K,
      original: UpdateEditFields[K]
    ): NonNullable<UpdateEditFields[K]> | UpdateEditFields[K] => {
      const edit = updateEdits[updateId];
      if (edit && field in edit) return edit[field] as UpdateEditFields[K];
      return original;
    },
    [updateEdits]
  );

  const setVersionField = useCallback(
    (versionId: string, field: keyof VersionEditFields, value: unknown) => {
      setVersionEdits(prev => ({
        ...prev,
        [versionId]: { ...prev[versionId], [field]: value },
      }));
    },
    []
  );

  const setUpdateField = useCallback(
    (updateId: string, field: keyof UpdateEditFields, value: unknown) => {
      setUpdateEdits(prev => ({
        ...prev,
        [updateId]: { ...prev[updateId], [field]: value },
      }));
    },
    []
  );

  const navigateBack = () => {
    navigate(
      `${ADMIN_PATH}/${adminPaths.regulatoryWatch}/${adminRegulatoryWatchPaths.references}${location.search}`
    );
  };

  const handleSave = useCallback(() => {
    if (!detail) return;
    const body: UpdateAdminReferenceDetailBody = {};

    if (refEdit.title !== detail.title) body.title = refEdit.title;
    if (refEdit.type !== (detail.type ?? null)) body.type = refEdit.type;
    if (refEdit.source !== detail.source.id) body.source = refEdit.source;
    const newUrl = refEdit.url || null;
    if (newUrl !== detail.url) body.url = newUrl;
    const newDesc = refEdit.description || null;
    if (newDesc !== detail.description) body.description = newDesc;

    const vPatches = Object.entries(versionEdits)
      .filter(([, e]) => Object.keys(e).length > 0)
      .map(([id, e]) => ({ id, ...e }));
    if (vPatches.length > 0) body.versions = vPatches;

    const uPatches = Object.entries(updateEdits)
      .filter(([, e]) => Object.keys(e).length > 0)
      .map(([id, e]) => ({ id, ...e }));
    if (uPatches.length > 0) body.updates = uPatches;

    if (Object.keys(body).length === 0) {
      navigateBack();
      return;
    }

    updateReferenceDetail(
      { id: detail.id, body },
      { onSuccess: () => navigateBack() }
    );
  }, [
    detail,
    refEdit,
    versionEdits,
    updateEdits,
    updateReferenceDetail,
    navigate,
  ]);

  if (isLoading) {
    return (
      <FlexCol gap="6" w="full" h="full" p="6" className="max-w-[1600px]">
        <p className="text-sm text-muted-foreground">Loading...</p>
      </FlexCol>
    );
  }

  if (!detail) {
    return (
      <FlexCol gap="6" w="full" h="full" p="6" className="max-w-[1600px]">
        <Button variant="ghost" onClick={navigateBack} className="w-fit gap-2">
          <ArrowLeft size={16} />
          Back to References
        </Button>
        <p className="text-sm text-muted-foreground">Reference not found.</p>
      </FlexCol>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-4 px-6 py-4 border-b bg-white shrink-0">
        <Button variant="ghost" onClick={navigateBack} className="gap-2">
          <ArrowLeft size={16} />
          Back
        </Button>
        <h2 className="text-xl font-semibold">{detail.title}</h2>
      </div>

      <div className="flex-1 min-h-0 flex gap-0 overflow-hidden bg-neutral-50">
        <VersionHistoryPanel
          versions={versions}
          selectedVersion={selectedVersion}
          isLoading={isLoading}
          isDeleting={isDeleting}
          onSelectVersion={setSelectedVersionId}
          onCreateVersion={() => setCreateModalOpen(true)}
          onDeleteVersion={id => {
            if (window.confirm('Delete this version? This cannot be undone.')) {
              deleteVersion(id);
            }
          }}
        />

        <VersionDetailPanel
          version={selectedVersion}
          isLoading={isLoading}
          getVersionField={getVersionField}
          setVersionField={setVersionField}
          getUpdateField={getUpdateField}
          setUpdateField={setUpdateField}
        />

        <ReferenceInfoPanel
          detail={detail}
          refEdit={refEdit}
          setRefEdit={setRefEdit}
          onSave={handleSave}
          onCancel={navigateBack}
          isSaving={isSaving}
        />
      </div>

      <CreateVersionModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        referenceId={referenceId!}
      />
    </div>
  );
};

const VersionHistoryPanel: FC<{
  versions: AdminReferenceDetailVersion[];
  selectedVersion: AdminReferenceDetailVersion | null;
  isLoading: boolean;
  isDeleting: boolean;
  onSelectVersion: (id: string) => void;
  onCreateVersion: () => void;
  onDeleteVersion: (id: string) => void;
}> = ({
  versions,
  selectedVersion,
  isLoading,
  isDeleting,
  onSelectVersion,
  onCreateVersion,
  onDeleteVersion,
}) => {
  return (
    <div className="w-[260px] shrink-0 bg-white border-r border-neutral-200 flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b">
        <span className="text-xs font-medium text-neutral-500 uppercase tracking-wider">
          Version history
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0"
          onClick={onCreateVersion}
        >
          <Plus size={14} />
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <p className="text-sm text-neutral-500 px-5 py-8 text-center">
            Loading...
          </p>
        ) : versions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3 text-neutral-500">
            <p className="text-sm">No versions yet</p>
            <Button variant="outline" size="sm" onClick={onCreateVersion}>
              <Plus size={12} className="mr-1" />
              Add Version
            </Button>
          </div>
        ) : (
          versions.map(v => (
            <div
              key={v.id}
              className={cn(
                'flex items-center gap-3 px-5 py-3.5 transition-colors cursor-pointer group border-b border-neutral-100',
                selectedVersion?.id === v.id
                  ? 'bg-neutral-50'
                  : 'hover:bg-neutral-50/50'
              )}
              onClick={() => onSelectVersion(v.id)}
            >
              <div className="flex flex-col gap-1 min-w-0 flex-1">
                <span className="text-sm font-medium text-neutral-900">
                  {v.effectiveDate
                    ? dayjs(v.effectiveDate.substring(0, 10)).format(
                        'DD/MM/YYYY'
                      )
                    : 'No date'}
                </span>
                <div className="flex items-center gap-1.5">
                  <Badge
                    variant="outline"
                    className="text-[10px] px-1.5 py-0 font-normal"
                  >
                    {v.status}
                  </Badge>
                  {v.sharedFile && (
                    <FileText size={11} className="text-neutral-400 shrink-0" />
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="p-1 rounded hover:bg-neutral-200 opacity-0 group-hover:opacity-100"
                      onClick={e => e.stopPropagation()}
                    >
                      <MoreHorizontal size={14} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => onDeleteVersion(v.id)}
                      disabled={isDeleting}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 size={12} className="mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <ChevronRight size={14} className="text-neutral-300" />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const VersionDetailPanel: FC<{
  version: AdminReferenceDetailVersion | null;
  isLoading: boolean;
  getVersionField: <K extends keyof VersionEditFields>(
    id: string,
    field: K,
    original: VersionEditFields[K]
  ) => VersionEditFields[K];
  setVersionField: (
    id: string,
    field: keyof VersionEditFields,
    value: unknown
  ) => void;
  getUpdateField: <K extends keyof UpdateEditFields>(
    id: string,
    field: K,
    original: UpdateEditFields[K]
  ) => UpdateEditFields[K];
  setUpdateField: (
    id: string,
    field: keyof UpdateEditFields,
    value: unknown
  ) => void;
}> = ({
  version,
  isLoading,
  getVersionField,
  setVersionField,
  getUpdateField,
  setUpdateField,
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'pdf'>('overview');

  useEffect(() => {
    setActiveTab('overview');
  }, [version?.id]);

  if (!version) {
    return (
      <div className="flex-1 min-w-0 max-w-[600px] bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-neutral-500">
          <FileText size={48} className="text-neutral-300" />
          <p className="text-sm">
            {isLoading ? 'Loading...' : 'Select a version to view details'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-w-0 max-w-[600px] bg-white flex flex-col overflow-hidden">
      <div className="flex items-center gap-1 px-8 pt-5 pb-0 shrink-0">
        <button
          onClick={() => setActiveTab('overview')}
          className={cn(
            'px-4 py-2 text-sm font-medium rounded-md transition-colors',
            activeTab === 'overview'
              ? 'text-neutral-900 bg-neutral-100'
              : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50'
          )}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab('pdf')}
          className={cn(
            'px-4 py-2 text-sm font-medium rounded-md transition-colors',
            activeTab === 'pdf'
              ? 'text-neutral-900 bg-neutral-100'
              : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50'
          )}
        >
          PDF
        </button>
      </div>

      {activeTab === 'overview' ? (
        <OverviewTab
          version={version}
          getVersionField={getVersionField}
          setVersionField={setVersionField}
          getUpdateField={getUpdateField}
          setUpdateField={setUpdateField}
        />
      ) : (
        <PdfTab version={version} />
      )}
    </div>
  );
};

const OverviewTab: FC<{
  version: AdminReferenceDetailVersion;
  getVersionField: <K extends keyof VersionEditFields>(
    id: string,
    field: K,
    original: VersionEditFields[K]
  ) => VersionEditFields[K];
  setVersionField: (
    id: string,
    field: keyof VersionEditFields,
    value: unknown
  ) => void;
  getUpdateField: <K extends keyof UpdateEditFields>(
    id: string,
    field: K,
    original: UpdateEditFields[K]
  ) => UpdateEditFields[K];
  setUpdateField: (
    id: string,
    field: keyof UpdateEditFields,
    value: unknown
  ) => void;
}> = ({
  version,
  getVersionField,
  setVersionField,
  getUpdateField,
  setUpdateField,
}) => {
  const update = version.update;
  const summaryRef = useRef<HTMLTextAreaElement>(null);

  const shortSummaryValue = update
    ? (((getUpdateField(update.id, 'shortSummary', update.shortSummary) ??
        update.shortSummary) as string | null) ?? '')
    : '';

  const summaryValue = update
    ? (((getUpdateField(update.id, 'summary', update.summary) ??
        update.summary) as string | null) ?? '')
    : '';

  useEffect(() => {
    const el = summaryRef.current;
    if (!el) return;
    const resize = () => {
      el.style.height = 'auto';
      el.style.height = `${Math.min(Math.max(el.scrollHeight, 160), 300)}px`;
    };
    resize();
    requestAnimationFrame(resize);
  }, [summaryValue]);

  const currentStatus = (getVersionField(
    version.id,
    'status',
    version.status
  ) ?? version.status) as string;
  const currentEffectiveDate = (getVersionField(
    version.id,
    'effectiveDate',
    version.effectiveDate
  ) ?? version.effectiveDate) as string | null;
  const currentVersionUrl = (getVersionField(version.id, 'url', version.url) ??
    version.url) as string | null;

  return (
    <div className="flex-1 overflow-y-auto px-8 py-6">
      <div className="mb-8">
        <h3 className="text-base font-semibold text-neutral-900 mb-4">
          Version Information
        </h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-neutral-500">
                Status
              </Label>
              <div>
                <StatusBadgeSelect
                  value={currentStatus}
                  onChange={v => setVersionField(version.id, 'status', v)}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-neutral-500">
                Effective Date
              </Label>
              <DatePickerInput
                value={
                  currentEffectiveDate
                    ? currentEffectiveDate.substring(0, 10)
                    : ''
                }
                onChange={v =>
                  setVersionField(version.id, 'effectiveDate', v || null)
                }
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-neutral-500">
              Version URL
            </Label>
            <Input
              value={currentVersionUrl ?? ''}
              onChange={e =>
                setVersionField(version.id, 'url', e.target.value || null)
              }
              placeholder="https://..."
            />
          </div>
        </div>
      </div>

      <div className="h-px w-full bg-neutral-200 mb-8" />

      <div>
        <h3 className="text-base font-semibold text-neutral-900 mb-4">
          Associated Update
        </h3>
        {update ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-neutral-500">
                  Update Type
                </Label>
                <div>
                  <UpdateTypeBadgeSelect
                    value={
                      (getUpdateField(
                        update.id,
                        'updateType',
                        update.updateType
                      ) ?? update.updateType) as string
                    }
                    onChange={v => setUpdateField(update.id, 'updateType', v)}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-neutral-500">
                  Publication Date
                </Label>
                <DatePickerInput
                  value={(
                    (getUpdateField(
                      update.id,
                      'publicationDate',
                      update.publicationDate
                    ) ?? update.publicationDate) as string
                  ).substring(0, 10)}
                  onChange={v =>
                    setUpdateField(update.id, 'publicationDate', v)
                  }
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-neutral-500">
                Short Summary
              </Label>
              <Input
                value={shortSummaryValue}
                onChange={e =>
                  setUpdateField(
                    update.id,
                    'shortSummary',
                    e.target.value || null
                  )
                }
                placeholder="Short summary of the update"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-neutral-500">
                Summary
              </Label>
              <Textarea
                ref={summaryRef}
                value={summaryValue}
                onChange={e =>
                  setUpdateField(update.id, 'summary', e.target.value || null)
                }
                className="min-h-[160px] max-h-[300px] resize-none overflow-y-auto"
                placeholder="Update summary..."
              />
            </div>
          </div>
        ) : (
          <p className="text-sm text-neutral-400">
            No update linked to this version
          </p>
        )}
      </div>
    </div>
  );
};

const PdfTab: FC<{ version: AdminReferenceDetailVersion }> = ({ version }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadFile, isPending: isUploading } =
    useAdminUploadVersionFileService();
  const { deleteFile, isPending: isDeletingFile } =
    useAdminDeleteVersionFileService();

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadFile({ versionId: version.id, file });
    e.target.value = '';
  };

  const handleDelete = () => {
    deleteFile(version.id);
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex items-center justify-between gap-3 px-6 py-2 border-b shrink-0">
        <div
          className="text-sm font-medium text-neutral-700 truncate min-w-0"
          title={version.sharedFile?.name}
        >
          {version.sharedFile ? version.sharedFile.name : 'No document'}
        </div>
        <div className="flex gap-2 shrink-0">
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            className="hidden"
            onChange={handleFileSelect}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            <Upload size={14} className="mr-1" />
            {version.sharedFile
              ? isUploading
                ? 'Replacing...'
                : 'Replace'
              : isUploading
                ? 'Uploading...'
                : 'Upload PDF'}
          </Button>
          {version.sharedFile && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDelete}
              disabled={isDeletingFile}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 size={14} className="mr-1" />
              {isDeletingFile ? 'Deleting...' : 'Delete'}
            </Button>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-hidden">
        {version.sharedFile ? (
          <iframe
            src={version.sharedFile.signedUrl}
            className="w-full h-full"
            title={version.sharedFile.name}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-neutral-500">
            <FileUp size={48} className="text-neutral-300" />
            <p className="text-sm">No document uploaded for this version</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              {isUploading ? 'Uploading...' : 'Upload PDF'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const ReferenceInfoPanel: FC<{
  detail: AdminReferenceDetailResponse;
  refEdit: RefEditState;
  setRefEdit: Dispatch<SetStateAction<RefEditState>>;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
}> = ({ refEdit, setRefEdit, onSave, onCancel, isSaving }) => {
  const { sources } = useAdminSourcesService();
  const descriptionRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = descriptionRef.current;
    if (!el) return;
    const resize = () => {
      el.style.height = 'auto';
      el.style.height = `${Math.min(Math.max(el.scrollHeight, 80), 300)}px`;
    };
    resize();
    requestAnimationFrame(resize);
  }, [refEdit.description]);

  return (
    <div className="w-[420px] shrink-0 bg-white border-l border-neutral-200 flex flex-col overflow-hidden">
      <div className="px-5 py-4 border-b">
        <span className="text-xs font-medium text-neutral-500 uppercase tracking-wider">
          Reference
        </span>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-neutral-600">Title</Label>
          <Input
            value={refEdit.title}
            onChange={e =>
              setRefEdit(prev => ({ ...prev, title: e.target.value }))
            }
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-neutral-600">Type</Label>
          <div>
            <TypeBadgeSelect
              value={refEdit.type}
              onChange={v => setRefEdit(prev => ({ ...prev, type: v }))}
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-neutral-600">Source</Label>
          <div>
            <SourceCombobox
              sources={sources}
              value={refEdit.source}
              onChange={v => setRefEdit(prev => ({ ...prev, source: v }))}
              badgeTrigger
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-neutral-600">URL</Label>
          <Input
            value={refEdit.url}
            onChange={e =>
              setRefEdit(prev => ({ ...prev, url: e.target.value }))
            }
            placeholder="https://..."
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-neutral-600">
            Description
          </Label>
          <Textarea
            ref={descriptionRef}
            value={refEdit.description}
            onChange={e =>
              setRefEdit(prev => ({ ...prev, description: e.target.value }))
            }
            className="min-h-[80px] max-h-[300px] resize-none overflow-y-auto"
            placeholder="Optional description..."
          />
        </div>
      </div>
      <div className="shrink-0 flex justify-end gap-2 px-5 py-4 border-t">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={onSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
};
